import { aggregateIngredients } from './ingredientNormalizer.js';

const SECTION_ORDER = ['produce', 'meat', 'dairy', 'bakery', 'pantry', 'spices'];
const SECTION_LABELS = {
  produce: '🥬 Produce',
  meat: '🥩 Meat & Seafood',
  dairy: '🧀 Dairy & Eggs',
  bakery: '🍞 Bakery',
  pantry: '🫙 Pantry',
  spices: '🧂 Spices & Condiments',
};

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MEALS = ['breakfast', 'lunch', 'dinner'];

// Aggregate arrays of recipe ingredients into a grouped, aisle-sorted list.
function aggregateAndSort(ingredientArrays) {
  return aggregateIngredients(ingredientArrays)
    .map(i => ({ ...i, checked: false }))
    .sort((a, b) =>
      SECTION_ORDER.indexOf(a.section) - SECTION_ORDER.indexOf(b.section) || a.item.localeCompare(b.item)
    );
}

/**
 * Aggregate ingredients from a legacy plan JSONB into a grouped shopping list.
 *
 * @param {Object} plan       - The plan JSONB from meal_plans
 * @param {Object} recipeMap  - { recipeId: recipe }
 * @returns {Array}           - [{ section, item, amount, unit, checked }] sorted by store aisle
 */
export function buildShoppingList(plan, recipeMap, filterDay = null) {
  const ingredientArrays = [];
  const days = filterDay ? [filterDay] : DAYS;

  days.forEach(day => {
    MEALS.forEach(meal => {
      const slot = plan[day]?.[meal];
      if (!slot) return;

      const recipeIds = slot.shared
        ? [slot.recipeId]
        : [slot.adultRecipeId, slot.kidRecipeId].filter(Boolean);

      [...new Set(recipeIds)].forEach(rid => {
        const r = recipeMap[rid];
        if (!r?.ingredients) return;

        const ingredients = Array.isArray(r.ingredients) ? r.ingredients : [];
        if (ingredients.length) ingredientArrays.push(ingredients);
      });
    });
  });

  return aggregateAndSort(ingredientArrays);
}

/**
 * Aggregate ingredients from normalized dinner_events (the board plan) into a
 * grouped shopping list. Skips open nights and free-text (recipe-less) events.
 *
 * @param {Array}  events     - dinner_events rows [{ recipe_id, status, ... }]
 * @param {Object} recipeMap  - { recipeId: recipe }
 * @returns {Array}           - [{ section, item, amount, unit, checked }] sorted by store aisle
 */
export function buildShoppingListFromEvents(events, recipeMap) {
  const ingredientArrays = [];

  (events || []).forEach(e => {
    if (!e.recipe_id) return; // open night or free-text — nothing to shop for
    const r = recipeMap[e.recipe_id];
    if (!r?.ingredients) return;
    const ingredients = Array.isArray(r.ingredients) ? r.ingredients : [];
    // One entry per night the recipe is cooked (cook it twice → buy twice).
    if (ingredients.length) ingredientArrays.push(ingredients);
  });

  return aggregateAndSort(ingredientArrays);
}

export { SECTION_ORDER, SECTION_LABELS };
